import { Typography, Box } from '@mui/material';
import { format } from 'date-fns';
import { FC } from 'react';

interface IDateRangeDisplay {
	start: Date | string;
	end: Date | string;
	description: string;
}

export const toDisplayTime = (date: Date) => {
	return format(new Date(date), 'HH:mm');
};

export const toDisplayDay = (date: Date) => {
	return format(new Date(date), 'dd MMM');
};

export const toDisplayYear = (date: Date) => {
	return format(new Date(date), 'yyyy');
};

export const calculateElapsedPercentage = (start: Date, end: Date) => {
	const now = new Date();
	const totalDuration = end.getTime() - start.getTime();
	const elapsedDuration = now.getTime() - start.getTime();
	return Math.min(100, Math.max(0, (elapsedDuration / totalDuration) * 100));
};

const DateRangeDisplay: FC<IDateRangeDisplay> = ({ start, end, description }) => {
	// Ensure start and end are Date objects
	const startDate = typeof start === 'string' ? new Date(start) : start;
	const endDate = typeof end === 'string' ? new Date(end) : end;

	// Handle cases where date conversion might fail
	if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
		return <Typography>Error: Invalid date(s) provided.</Typography>;
	}

	const elapsedPercentage = calculateElapsedPercentage(startDate, endDate);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'center',
				width: '100%',
			}}
		>
			<Box>
				<Box sx={{ pr: 2 }}>
					<Typography sx={{ fontSize: 14 }}>{toDisplayDay(startDate)}</Typography>
					<Typography sx={{ fontSize: 30, fontWeight: 'bold', lineHeight: 1 }}>{toDisplayYear(startDate)}</Typography>
					<Typography sx={{ fontSize: 14 }}>{toDisplayTime(startDate)}</Typography>
				</Box>
			</Box>
			<Box sx={{ flex: 1, textAlign: 'center' }}>
				<Typography sx={{ fontSize: 14 }}>{description}</Typography>

				<Box
					sx={{
						position: 'relative',
						backgroundColor: '#e0e0e0',
						width: '100%',
						height: 5,
						overflow: 'hidden',
					}}
				>
					<Box
						sx={{
							position: 'absolute',
							backgroundColor: 'secondary.main',
							width: `${elapsedPercentage}%`,
							height: '100%',
							transition: 'width 0.2s',
						}}
					/>
				</Box>
			</Box>
			<Box>
				<Box sx={{ pl: 2 }}>
					<Typography sx={{ fontSize: 14 }}>{toDisplayDay(endDate)}</Typography>
					<Typography sx={{ fontSize: 30, fontWeight: 'bold', lineHeight: 1 }}>{toDisplayYear(endDate)}</Typography>
					<Typography sx={{ fontSize: 14 }}>{toDisplayTime(endDate)}</Typography>
				</Box>
			</Box>
		</Box>
	);
};

export default DateRangeDisplay;
